import type { PageRouteSettings, TaskNavigateOptions } from '@Core/lib/tasks/utils/types';

import type { PageRef, SdkPageHandle, SdkUtils } from './types';

/**
 * The handle `Sdk.page(id)` returns. It exists for any id: the stored source
 * is validated against the workspace's pages at save time, and an unpublished
 * page must still render in the editor preview, so an unknown or unpublished
 * id degrades the same way `Sdk.utils.page.url` does instead of throwing.
 */
export function createPageHandle(
  id: string,
  pageUtils: SdkUtils['page'],
  settings: PageRouteSettings
): SdkPageHandle {
  return {
    id,
    route: settings?.[id]?.route ?? null,
    url: (params) => pageUtils.url({ id, params }),
    navigate: (params, options) => pageUtils.navigate({ id, params }, options)
  };
}

/**
 * The `Pages` map of a file-based workspace: `Pages.<key>` is the page with
 * that key in the deployed page route catalog, which leaves out paused pages.
 * A key that the catalog does not list gives a handle without an id or route,
 * as `Sdk.page()` does for an unknown id.
 */
export function createPagesByKey(
  settings: PageRouteSettings,
  pageUtils: SdkUtils['page']
): Readonly<Record<string, PageRef>> {
  const idsByKey = new Map<string, string>();

  for (const [id, entry] of Object.entries(settings ?? {})) {
    if (typeof entry?.key === 'string') {
      idsByKey.set(entry.key, id);
    }
  }

  return new Proxy(Object.create(null) as Record<string, PageRef>, {
    get: (_target, key) => {
      if (typeof key !== 'string') {
        return undefined;
      }

      const id = idsByKey.get(key);
      // `Sdk.utils.page` warns that a target without a route has none and
      // returns null, so a missing key reaches it under its `Pages.` name.
      const handle = createPageHandle(id ?? `Pages.${key}`, pageUtils, settings);

      return {
        key,
        id: id ?? null,
        route: id === undefined ? null : handle.route,
        url: (params?: Record<string, unknown>) => handle.url(params),
        navigate: (params?: Record<string, unknown>, options?: TaskNavigateOptions) =>
          handle.navigate(params, options)
      };
    }
  });
}
